import React, { useState, Children } from 'react';
import { Header } from '../components/Header';
import { ProgressIndicator } from '../components/ProgressIndicator';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { addMonths, subMonths, addYears, subYears, getDaysInMonth, startOfMonth, getDay, isSameDay, format } from 'date-fns';
interface ScheduleInspectionScreenProps {
  onConfirm: () => void;
  onBack: () => void;
}
const daysOfWeek = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
export function ScheduleInspectionScreen({
  onConfirm,
  onBack
}: ScheduleInspectionScreenProps) {
  const [selectedTime, setSelectedTime] = useState<string>('10:30 AM');
  const [viewDate, setViewDate] = useState<Date>(new Date(2022, 9, 1)); // October 2022
  const [selectedDate, setSelectedDate] = useState<Date>(new Date(2022, 9, 8));
  const [navDirection, setNavDirection] = useState<number>(0);
  const today = new Date();
  const totalDays = getDaysInMonth(viewDate);
  // Monday-first offset
  const leadingBlanks = (getDay(startOfMonth(viewDate)) + 6) % 7;
  const cells: (number | null)[] = [...Array.from({
    length: leadingBlanks
  }, () => null), ...Array.from({
    length: totalDays
  }, (_, i) => i + 1)];
  const goMonth = (dir: number) => {
    setNavDirection(dir);
    setViewDate(d => dir > 0 ? addMonths(d, 1) : subMonths(d, 1));
  };
  const goYear = (dir: number) => {
    setNavDirection(dir);
    setViewDate(d => dir > 0 ? addYears(d, 1) : subYears(d, 1));
  };
  const timeSlots = [{
    time: '10:30 AM',
    label: 'Jenna Smith | Manager'
  }, {
    time: '12:00 PM',
    label: 'Jenna Smith | Manager'
  }];
  const monthKey = format(viewDate, 'yyyy-MM');
  return <div className="h-full flex flex-col overflow-hidden" data-id="element-154">
      <div className="flex-none" data-id="element-155">
        <Header title="Schedule Your Final Inspection" onBack={onBack} data-id="element-156" />
        <ProgressIndicator currentStep={3} data-id="element-157" />
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-2" data-id="element-158">
        {/* Calendar */}
        <div className="bg-white rounded-2xl p-5 shadow-sm mb-6 border border-gray-100" data-id="element-159">
          {/* Nav */}
          <div className="flex items-center justify-between mb-5" data-id="element-160">
            <div className="flex items-center gap-0.5" data-id="element-161">
              <button onClick={() => goYear(-1)} aria-label="Previous year" className="p-1.5 rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-[#0F4C3A]" data-id="element-162">
                <ChevronsLeft className="w-4 h-4" data-id="element-163" />
              </button>
              <button onClick={() => goMonth(-1)} aria-label="Previous month" className="p-1.5 rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-[#0F4C3A]" data-id="element-164">
                <ChevronLeft className="w-4 h-4" data-id="element-165" />
              </button>
            </div>

            <div className="relative overflow-hidden h-6 flex-1 mx-2" data-id="element-166">
              <AnimatePresence mode="wait" initial={false} data-id="element-167">
                <motion.h3 key={monthKey} initial={{
                opacity: 0,
                x: navDirection >= 0 ? 14 : -14
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: navDirection >= 0 ? -14 : 14
              }} transition={{
                duration: 0.2,
                ease: [0.22, 1, 0.36, 1]
              }} className="absolute inset-0 text-center font-bold text-gray-900 text-base" data-id="element-168">
                  {format(viewDate, 'MMMM yyyy')}
                </motion.h3>
              </AnimatePresence>
            </div>

            <div className="flex items-center gap-0.5" data-id="element-169">
              <button onClick={() => goMonth(1)} aria-label="Next month" className="p-1.5 rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-[#0F4C3A]" data-id="element-170">
                <ChevronRight className="w-4 h-4" data-id="element-171" />
              </button>
              <button onClick={() => goYear(1)} aria-label="Next year" className="p-1.5 rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-[#0F4C3A]" data-id="element-172">
                <ChevronsRight className="w-4 h-4" data-id="element-173" />
              </button>
            </div>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-x-1 mb-2" data-id="element-174">
            {daysOfWeek.map((day, i) => <div key={day} className={`text-center text-xs font-semibold uppercase tracking-wider ${i >= 5 ? 'text-gray-300' : 'text-gray-400'}`} data-id="element-175">
                {day}
              </div>)}
          </div>

          {/* Day grid (re-mounts per month for entrance animation) */}
          <AnimatePresence mode="wait" initial={false} data-id="element-176">
            <motion.div key={monthKey} initial={{
            opacity: 0
          }} animate={{
            opacity: 1,
            transition: {
              staggerChildren: 0.01,
              delayChildren: 0.02
            }
          }} exit={{
            opacity: 0,
            transition: {
              duration: 0.12
            }
          }} className="grid grid-cols-7 gap-y-3 gap-x-1" data-id="element-177">
              {cells.map((day, index) => {
              const cellDate = day ? new Date(viewDate.getFullYear(), viewDate.getMonth(), day) : null;
              const isSelected = cellDate ? isSameDay(cellDate, selectedDate) : false;
              const isToday = cellDate ? isSameDay(cellDate, today) : false;
              const col = index % 7;
              const isWeekend = col >= 5;
              return <motion.div key={index} variants={{
                hidden: {
                  opacity: 0,
                  scale: 0.6
                },
                show: {
                  opacity: 1,
                  scale: 1
                }
              }} initial="hidden" animate="show" transition={{
                type: 'spring',
                stiffness: 400,
                damping: 24
              }} className="relative flex items-center justify-center aspect-square" data-id="element-178">
                    {day && cellDate ? <motion.button whileHover={{
                  scale: isSelected ? 1 : 1.12
                }} whileTap={{
                  scale: 0.9
                }} onClick={() => setSelectedDate(cellDate)} className={`relative z-10 w-9 h-9 flex items-center justify-center text-sm font-medium rounded-full transition-colors ${isSelected ? 'text-white' : isToday ? 'text-[#0F4C3A] ring-1 ring-[#0F4C3A]/40' : isWeekend ? 'text-gray-400 hover:bg-gray-100' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-179">
                        {isSelected && <motion.div key="sel" initial={{
                    scale: 0.4,
                    opacity: 0
                  }} animate={{
                    scale: 1,
                    opacity: 1
                  }} transition={{
                    type: 'spring',
                    stiffness: 500,
                    damping: 28
                  }} className="absolute inset-0 bg-[#0F4C3A] rounded-full -z-10 shadow-md shadow-[#0F4C3A]/30" data-id="element-180" />}
                        {day}
                      </motion.button> : <div className="w-9 h-9" data-id="element-181" />}
                  </motion.div>;
            })}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Available Times */}
        <div className="mb-4" data-id="element-182">
          <h3 className="font-bold text-gray-900 mb-4 px-1" data-id="element-183">Available Times</h3>
          <div className="space-y-3" data-id="element-184">
            {timeSlots.map(slot => {
            const isSelected = selectedTime === slot.time;
            return <motion.label key={slot.time} whileTap={{
              scale: 0.98
            }} className={`flex items-center p-4 rounded-xl cursor-pointer border-2 transition-all duration-200 ${isSelected ? 'border-[#0F4C3A] bg-[#0F4C3A]/5 shadow-sm' : 'border-gray-100 bg-white hover:border-gray-200 shadow-sm'}`} data-id="element-185">
                  <div className="relative flex items-center justify-center w-5 h-5 mr-4 flex-shrink-0" data-id="element-186">
                    <input type="radio" name="time" checked={isSelected} onChange={() => setSelectedTime(slot.time)} className="peer sr-only" data-id="element-187" />
                    <div className={`w-5 h-5 rounded-full border-2 transition-colors ${isSelected ? 'border-[#0F4C3A]' : 'border-gray-300'}`} data-id="element-188" />
                    <motion.div initial={false} animate={{
                  scale: isSelected ? 1 : 0
                }} className="absolute w-2.5 h-2.5 bg-[#0F4C3A] rounded-full" data-id="element-189" />
                  </div>
                  <div className="flex-1" data-id="element-190">
                    <div className={`font-bold text-base transition-colors ${isSelected ? 'text-[#0F4C3A]' : 'text-gray-900'}`} data-id="element-191">
                      {slot.time}
                    </div>
                    <div className="text-xs text-gray-500 font-medium mt-0.5" data-id="element-192">
                      {slot.label}
                    </div>
                  </div>
                  {isSelected && <motion.div initial={{
                scale: 0,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                type: 'spring',
                stiffness: 300,
                damping: 20
              }} data-id="element-193">
                      <CheckCircle2 className="w-5 h-5 text-[#0F4C3A]" data-id="element-194" />
                    </motion.div>}
                </motion.label>;
          })}
          </div>
        </div>
      </div>

      {/* Footer Button */}
      <div className="flex-none p-6 pt-2 pb-8 bg-[#F7F8F7]" data-id="element-195">
        <motion.button whileTap={{
        scale: 0.97
      }} onClick={onConfirm} className="w-full bg-[#0F4C3A] text-white py-4 rounded-xl font-semibold text-lg hover:bg-[#134E3A] transition-colors shadow-md" data-id="element-196">
          Confirm
        </motion.button>
      </div>
    </div>;
}